import type { DrawingDocument } from '@peruselab/structure-editor/model';
import type { ConditionFields } from './reactionConditions';

/**
 * #1165 layer 2: the paper's unsaved work, kept on this device so it can be restored after a
 * refresh, a crash or a closed tab. Never sent to the server, never a version: one draft per
 * person and space (the active tenant's scope key), the paper as it was last.
 */
export interface UnsavedDraft {
  /** When it was kept (ISO 8601). */
  readonly keptAt: string;
  readonly title: string;
  readonly document: DrawingDocument;
  /** A reaction's conditions and catalysts, held beside the drawing (useReactionDetails). */
  readonly conditionFields: ConditionFields;
  readonly catalystAtomIds: readonly string[];
  /** The saved drawing or reaction it was opened from, at the version it was opened at. */
  readonly from?: { readonly kind: 'drawing' | 'reaction'; readonly id: string; readonly revision: number };
}

export interface UnsavedDraftStore {
  read(key: string): Promise<UnsavedDraft | null>;
  write(key: string, draft: UnsavedDraft): Promise<void>;
  clear(key: string): Promise<void>;
}

const DATABASE = 'peruselab-drafts';
const STORE = 'drafts';

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE, 1);
    request.onupgradeneeded = () => request.result.createObjectStore(STORE);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function run<T>(mode: IDBTransactionMode, act: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const database = await openDatabase();
  try {
    return await new Promise<T>((resolve, reject) => {
      const request = act(database.transaction(STORE, mode).objectStore(STORE));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  } finally {
    database.close();
  }
}

/**
 * The browser's IndexedDB. Storage can be missing or refused (a private window, blocked site
 * data): then there is simply no draft -- every call resolves, none throws.
 */
export const indexedDbDraftStore: UnsavedDraftStore = {
  async read(key) {
    try {
      return ((await run('readonly', (store) => store.get(key))) as UnsavedDraft | undefined) ?? null;
    } catch {
      return null;
    }
  },
  async write(key, draft) {
    try {
      await run('readwrite', (store) => store.put(draft, key));
    } catch {
      // No storage: no draft.
    }
  },
  async clear(key) {
    try {
      await run('readwrite', (store) => store.delete(key));
    } catch {
      // No storage: nothing to clear.
    }
  }
};
